<template>
  <div class="text-editor-container">
    <!-- Enable Toggle -->
    <b-form-checkbox
      v-if="showEnable"
      v-model="enable"
      name="summary-toggle"
      switch
      @input="changeEnable"
      class="mb-3"
    >
      <p class="font-weight-bold mb-0">
        {{ $t(`pages.chatSettings.ticketEditor.enable.${enable}`) }}
      </p>
    </b-form-checkbox>

    <!-- Description -->
    <b-card-text class="mb-1 mt-1">
      {{ desc }}
    </b-card-text>

    <!-- Main Editor Section -->
    <div class="editor-wrapper mb-2">
      <h5 class="mb-2" v-if="editorTitle">{{ editorTitle }}</h5>
      <div class="editor-content">
        <div class="textarea-container">
          <b-form-textarea
            v-model="currentLocaleText"
            rows="6"
            no-resize
            class="flex-grow-1"
            @keydown="handleKeyDown"
          ></b-form-textarea>
        </div>
        <div class="language-selector">
          <b-form-select
            v-model="locale"
            :options="locales"
            class="language-select"
          ></b-form-select>
        </div>
      </div>
    </div>

    <!-- Time Settings Section -->
    <div class="time-settings">
      <!-- Close Time Input -->
      <div
        class="time-input-group mb-3"
        v-if="closeTime !== undefined"
      >
        <b-form-input
          v-model="closeTime"
          type="number"
          class="time-input"
          @keydown="handleKeyDown"
        ></b-form-input>
        <span class="time-unit">
          <strong>{{ $t("pages.chatSettings.ticketEditor.close.unit") }}</strong>
        </span>
        <span class="time-desc">
          {{ $t("pages.chatSettings.ticketEditor.close.title") }}
        </span>
      </div>

      <!-- Send After Input -->
      <div
        class="time-input-group"
        v-if="sendAfter !== undefined"
      >
        <b-form-input
          v-model="sendAfter"
          type="number"
          class="time-input"
          @keydown="handleKeyDown"
        ></b-form-input>
        <span class="time-desc">
          {{ $t("pages.chatSettings.ticketEditor.unresponsive.desc") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCardText,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BFormTextarea,
} from "bootstrap-vue";
import _isEqual from "lodash/isEqual";

export default {
  name: 'TextEditor',
  components: {
    BCardText,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BFormTextarea,
  },
  props: {
    id: {
      type: String,
      default: "",
    },
    value: {
      type: Object,
      default: () => ({
        closeTime: undefined,
        sendAfter: undefined,
        enable: false,
        locales: {
          en: "",
          zh: "",
          km: "",
          vi: "",
        },
      }),
    },
    title: {
      type: String,
      default: "",
    },
    desc: {
      type: String,
      required: true,
    },
    editorTitle: {
      type: String,
      default: undefined,
    },
    showEnable: {
      type: Boolean,
      default: true,
    }
  },
  data: () => ({
    locale: "en",
    locales: [
      { value: "en", text: "EN" },
      { value: "zh", text: "CN" },
      { value: "km", text: "KH" },
      { value: "vi", text: "VN" },
    ],
    sendAfter: undefined,
    closeTime: undefined,
    originValue: false,
    enable: false,
    en: "",
    zh: "",
    km: "",
    vi: "",
    pressingCount: 0,
  }),
  computed: {
    currentLocaleText: {
      get() {
        return this[this.locale];
      },
      set(value) {
        this[this.locale] = value;
      }
    },
    mappedReturnValue() {
      let value = {
        enable: this.enable,
        locales: {
          en: this.en,
          zh: this.zh,
          km: this.km,
          vi: this.vi,
        },
      };
      if (this.sendAfter !== undefined) value.sendAfter = this.sendAfter;
      if (this.closeTime !== undefined) value.closeTime = this.closeTime;
      return value;
    },
  },
  watch: {
    value: {
      handler(newValue) {
        if (!_isEqual(this.mappedReturnValue, newValue)) {
          this.mapLocalesToData();
        }
      },
      deep: true
    },
    mappedReturnValue: {
      handler(newValue) {
        if (!_isEqual(this.value, newValue)) {
          this.$emit("input", newValue);
        }
      },
      deep: true
    },
  },
  methods: {
    handleKeyDown() {
      let timeoutId = 0;
      this.pressingCount += 1;
      const currentPressingCount = this.pressingCount;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        if (currentPressingCount === this.pressingCount) {
          const payload = { ...this.value, id: this.id };
          this.$emit("update:textEditor", payload);
          this.pressingCount = 0;
        }
      }, 3000);
    },
    changeEnable() {
      if (this.originValue === this.enable) return;
      this.originValue = this.enable;
      const payload = {
        enable: this.enable,
        id: this.id,
      };
      this.$emit("update:enable", payload);
    },
    mapLocalesToData() {
      const { enable, locales, sendAfter, closeTime } = this.value;
      const { en, zh, km, vi } = locales;
      if (sendAfter !== undefined) this.sendAfter = sendAfter;
      if (closeTime !== undefined) this.closeTime = closeTime;
      this.enable = enable;
      this.originValue = enable;
      this.en = en;
      this.zh = zh;
      this.km = km;
      this.vi = vi;
    },
  },
  mounted() {
    this.mapLocalesToData();
  },
};
</script>

<style lang="scss" scoped>
.text-editor-container {
  width: 100%;
  max-width: 100%;
}

.editor-wrapper {
  width: 100%;
}

.editor-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
}

.textarea-container {
  flex-grow: 1;
  width: 100%;
}

.language-selector {
  width: 100%;
  max-width: 120px;

  @media (max-width: 767px) {
    max-width: 100%;
  }
}

.language-select {
  width: 100%;
  min-width: 72px;
}

.time-settings {
  width: 100%;
}

.time-input-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.time-input {
  width: 80px !important;
  min-width: 60px !important;
}

.time-unit {
  white-space: nowrap;
}

.time-desc {
  margin-left: 0.5rem;
}

@media (max-width: 576px) {
  .time-input-group {
    flex-direction: column;
    align-items: flex-start;

    .time-desc {
      margin-left: 0;
    }
  }
}
</style>